import React, { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { ModuleRegistry } from "ag-grid-community";
import { AllCommunityModule, ClientSideRowModelModule, CsvExportModule } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { Toast } from "../../../config/Toast";
import { ColumnBPJS } from "./Column";
import { FormatSalaryItem } from "../../../helper/ResponseSalary";
import ActionBPJS from "./action";

import Pagination from "../../../components/Pagination";
import Button from "../../../components/Button";

// modals
import Posting from "./modals/Posting";
import Submit from "./modals/Submit";
import Detail from "./modals/Detail";

// Services
import BookmarkSalaryService from "../../../services/BookmarkSalary";
import BpjsService from "../../../services/BpjsDeduction";
import MasterBpjsService from "../../../services/MasterBpjs";

import "react-loading-skeleton/dist/skeleton.css";

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule, CsvExportModule]);

function SalaryCutBPJS() {
  const [rowData, setRowData] = useState([]);
  const [masterBpjs, setMasterBpjs] = useState(null);
  const [dataDetail, setDataDetail] = useState(null);
  const [dataFinal, setDataFinal] = useState([]);
  const [dataIdSalary, setDataIdSalary] = useState(null);

  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [payloadSort, setPayloadSort] = useState(null);
  const moduleBookmark = "bpjs";

  const getLocalStorage = (key) => {
    const localStorageData = localStorage.getItem(key);
    return localStorageData ? JSON.parse(localStorageData) : null;
  };

  const users = getLocalStorage("rms") || { id: 0 };

  // Modal
  const [activeModals, setActiveModals] = useState({
    posting: false,
    detail: false,
    edit: false,
    submit: false,
  });

  const openModal = (name) => setActiveModals((prev) => ({ ...prev, [name]: true }));
  const closeModal = (name) => setActiveModals((prev) => ({ ...prev, [name]: false }));

  const limitDataPages = 10;

  // Toast Notification
  const notifySuccess = useCallback((message) => toast.success(message, Toast), []);
  const notifyFailed = useCallback((message) => toast.error(message, Toast), []);

  // Fetch Master BPJS
  const fetchMasterBpjs = useCallback(async () => {
    try {
      const { data } = await MasterBpjsService.list();
      setMasterBpjs(data.data.amount);
    } catch (err) {
      console.error("Master BPJS error:", err);
      notifyFailed("Gagal memuat data master BPJS");
    }
  }, [notifyFailed]);

  // Fetch BPJS
  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    const sort_by = payloadSort?.sort_by || null;
    const sort_type = payloadSort?.sort_type || null;

    try {
      const res = await BpjsService.list(pageNumber, limitDataPages, search, startDate, endDate, sort_by, sort_type);
      const { data = [], total_page = 0 } = res.data;

      const formatted = FormatSalaryItem(data, pageNumber, limitDataPages);
      setRowData(formatted);
      setTotalPage(total_page);
    } catch (err) {
      console.error("Fetch BPJS error:", err);
      setRowData([]);
      setTotalPage(0);
      notifyFailed(err.response.data.message || "Gagal memuat data");
    } finally {
      setLoading(false);
    }
  }, [pageNumber, search, startDate, endDate, payloadSort, notifyFailed]);

  // Fetch Save and Delete BPJS
  const handleSaveBpjs = useCallback(async () => {
    try {
      if (!Array.isArray(dataFinal) || dataFinal.length === 0) {
        return notifyFailed("Tidak ada data BPJS yang disimpan");
      }

      const finalPayload = {
        items: dataFinal.map((item) => {
          return {
            payment_status: item.payment_status ? item.payment_status : "",
            payment_date: item.payment_date ? item.payment_date : null,
            month: item.month ? item.month : null,
            year: item.year ? item.year : null,
            note: item.note ? item.note : null,
          };
        }),
      };

      await BpjsService.update(dataIdSalary, finalPayload);

      notifySuccess("Berhasil melakukan pembayaran BPJS");
      closeModal("submit");
      closeModal("detail");
      fetchData();
    } catch (err) {
      console.error("Update BPJS error:", err);
      notifyFailed(err?.response?.data?.message || "Gagal melakukan pembayaran BPJS");
    }
  }, [dataFinal, dataIdSalary, fetchData, notifySuccess, notifyFailed]);

  // UseEffect Master BPJS
  useEffect(() => {
    fetchMasterBpjs();
  }, [fetchMasterBpjs]);

  // UseEffect BPJS
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePostingConfirm = useCallback(async () => {
    if (!startDate || !endDate) return notifyFailed("Periode tanggal tidak boleh kosong");
    setLoadingPost(true);
    try {
      await BpjsService.post({ start_date: startDate, end_date: endDate });
      notifySuccess("Berhasil posting data BPJS tukang");
      fetchData();
    } catch (err) {
      console.error("Posting error:", err);
      notifyFailed("Gagal posting data BPJS tukang");
    } finally {
      setLoadingPost(false);
    }
  }, [startDate, endDate, fetchData, notifySuccess, notifyFailed]);

  const handleToggleBookmark = useCallback(
    async (id, currentBookmark, bookMarkValue) => {
      setRowData((prev) =>
        prev.map((r) => {
          if (r.id === id) {
            const newBookmark = r.isBookmark === 1 ? 0 : 1;
            return { ...r, isBookmark: newBookmark };
          }
          return r;
        })
      );
      try {
        await BookmarkSalaryService.bookmark(id, bookMarkValue === 1 ? 0 : 1, moduleBookmark);
        notifySuccess("Berhasil memperbarui bookmark");
      } catch (err) {
        setRowData((prev) => prev.map((r) => (r.id === id ? { ...r, bookmark: currentBookmark } : r)));
        console.error("confirm bookmark error:", err);
        notifyFailed("Gagal memperbarui bookmark");
      } finally {
        fetchData({ page: pageNumber });
      }
    },
    [notifyFailed, notifySuccess, pageNumber, fetchData]
  );

  const handleDateChange = useCallback((dates) => {
    if (dates?.length === 2) {
      setStartDate(moment(dates[0]).format("YYYY-MM-DD"));
      setEndDate(moment(dates[1]).format("YYYY-MM-DD"));
      setPageNumber(1);
    }
  }, []);

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPageNumber(1);
  }, []);

  const handleFilter = useCallback(async () => {
    if (!startDate || !endDate) return notifyFailed("Periode rentang waktu tidak boleh kosong");
    setLoadingFilter(true);
    await fetchData();
    setLoadingFilter(false);
  }, [startDate, endDate, fetchData, notifyFailed]);

  const handleChangePage = useCallback(({ selected }) => {
    setPageNumber(selected + 1);
  }, []);

  const handleSortOrder = useCallback((payload) => {
    setPayloadSort(payload);
  }, []);

  const handleOpenPosting = useCallback(() => {
    if (!startDate || !endDate) return notifyFailed("Periode tanggal tidak boleh kosong");
    openModal("posting");
  }, [startDate, endDate, notifyFailed]);

  const handleDetailModal = useCallback((id, worker_name, rowData, idSalary) => {
    rowData.information = { id, worker_name };
    setDataDetail(rowData);
    setDataIdSalary(idSalary);
    openModal("detail");
  }, []);

  const handleSubmitDataBpjs = useCallback((listData) => {
    const updatedList = listData
      .map((item) => {
        const paymentDate = item.payment_date ? item.payment_date : null;
        const year = item.year ? item.year : null;

        return {
          payment_status: item.payment_status || "on_process",
          payment_date: paymentDate,
          year,
          month: item.month || null,
          note: item.note || "",
        };
      })
      .filter((item) => item.year && item.payment_date && item.payment_status !== "paid");

    setDataFinal(updatedList);
    closeModal("edit");
    openModal("submit");
  }, []);

  const handleDeleteDataBpjs = useCallback((listData) => {
    setDataFinal(listData);
    closeModal("edit");
    openModal("submit");
  }, []);

  const handleSwap = useCallback(
    async (isTopup, isSalaryId) => {
      try {
        const topUp = isTopup === 1 ? 0 : 1;

        const payload = {
          is_topup: topUp,
          processed_by: users.id,
        };

        setDataIdSalary(isSalaryId);

        await BpjsService.topup(isSalaryId, payload);
        fetchData();
      } catch (err) {
        console.error("Topup BPJS error:", err);
        notifyFailed(err.response?.data?.message);
      }
    },
    [fetchData, notifyFailed]
  );

  const ActionRenderer = useCallback(
    (params) => {
      const row = params.data;
      const isPosting = row.isPosting === 1;
      const bookMarkValue = row.isBookmark;
      const bookmark = row.isBookmark === 1 ? "btn-success" : "btn-outline-success";

      return (
        <Fragment>
          {!isPosting ? (
            <>
              <Button
                button={`${bookmark} btn-sm me-1`}
                icon="fa fa-bookmark"
                onClick={() => handleToggleBookmark(row.id, bookmark, bookMarkValue)}
              />
              <Button
                button="btn-outline-warning btn-sm"
                icon="fa fa-wrench text-warning"
                onClick={() => handleDetailModal(row.workerId, row.workerName, row, row.id)}
              />
            </>
          ) : (
            <Button
              button="btn-outline-warning btn-sm"
              icon="fa fa-wrench text-warning"
              onClick={() => handleDetailModal(row.workerId, row.workerName, row, row.id)}
            />
          )}
        </Fragment>
      );
    },
    [handleToggleBookmark, handleDetailModal]
  );

  const depositBalance = useCallback(
    (params) => {
      const data = params.data;
      const value = data?.depositBalance;
      const isCanSwap = data?.canSwap;
      const isTopup = data?.isTopup;
      const isSalaryId = data?.id;
      return (
        <div className="flex flex-row justify-content-between align-items-center w-full">
          <div>
            <span className="font-semibold text-blue-600">Rp {value?.toLocaleString()}</span>
          </div>
          <div>
            <Button
              button={!isCanSwap ? "btn-secondary btn-sm pointer-events-none bg-secondary" : "btn-outline-warning btn-sm"}
              icon={!isCanSwap ? "fa fa-exchange white-color" : "fa fa-exchange text-warning"}
              onClick={() => handleSwap?.(isTopup, isSalaryId)}
            />
          </div>
        </div>
      );
    },
    [handleSwap]
  );

  const colDefs = useMemo(
    () => ColumnBPJS(ActionRenderer, handleSortOrder, depositBalance, payloadSort),
    [ActionRenderer, handleSortOrder, depositBalance, payloadSort]
  );

  return (
    <div className="w-100 d-flex flex-column gap-3">
      {activeModals.posting && (
        <Posting activeModal closeModal={() => closeModal("posting")} onConfirm={handlePostingConfirm} />
      )}

      {activeModals.detail && (
        <Detail
          activeModal
          closeModal={() => closeModal("detail")}
          data={dataDetail}
          masterBpjs={masterBpjs}
          submitBpjsHandler={handleSubmitDataBpjs}
          deleteHandler={handleDeleteDataBpjs}
        />
      )}

      {activeModals.submit && (
        <Submit activeModal closeModal={() => closeModal("submit")} saveBpjsHandler={handleSaveBpjs} />
      )}

      <ActionBPJS
        rangeDate={[startDate ? moment(startDate).toDate() : null, endDate ? moment(endDate).toDate() : null]}
        onDateChange={(dates) => handleDateChange(dates)}
        onFilter={handleFilter}
        onPost={handleOpenPosting}
        onSearch={handleSearch}
        startDate={startDate}
        endDate={endDate}
        loadingFilter={loadingFilter}
        loadingPost={loadingPost}
      />

      <div
        className="ag-theme-alpine w-100"
        style={{
          height: 50 * rowData.length + 50 + "px",
          maxHeight: 50 * rowData.length + 50 + "px",
          minHeight: "250px",
          overflow: "auto",
        }}
      >
        <AgGridReact
          rowData={loading ? rowData : rowData}
          columnDefs={colDefs}
          domLayout="normal"
          theme="legacy"
          rowHeight={50}
          headerHeight={50}
          animateRows
          enableCellTextSelection
          defaultColDef={{ sortable: false, filter: false, resizable: true }}
          overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Memuat data...</span>'}
          overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Tidak ada data</span>'}
          getRowStyle={(params) => {
            switch (params.data?.isPosting) {
              case 1:
                return { color: "#7041c1" };
              case 0:
                return { color: "#000000" };
              default:
                return { backgroundColor: "transparent" };
            }
          }}
        />
      </div>

      {totalPage > 0 && <Pagination pageCount={totalPage} forcePage={pageNumber - 1} changePage={handleChangePage} />}
    </div>
  );
}

export default SalaryCutBPJS;
